// React
import { useDocumentTitle } from '@refinedev/react-router-v6';
import React, { useState } from 'react';

// Mantine
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_RowSelectionState,
} from 'mantine-react-table';
import { Checkbox } from '@mantine/core';

// Custom Components
import { EditRowButton } from 'components/common/EditRowButton';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { TableProgressBar } from 'components/common/TableProgressBar';

// Interface
import { Organization, Role, User } from 'interfaces';

// Columns
import { UserColumns } from 'columns/User';

// Store
import { useCreateUser, useDisableUser, useUpdateUser } from 'api/resource';
import { useResendUserPassword } from 'api/action';

// Store
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { UserFormModal } from 'components/users/UserFormModal';
import { useIsAdmin, useCanModify } from 'hooks';
import { UserToolBar } from './components/UserTopToolBar';

/**
 * TODO:
 * - Add predefined SITES to sitesData.tsx, treat sites similar to tariffs, multiple can be added to a user
 * - Modify the usersData.tsx to change column editing (some dropdowns, etc.)
 * - Expand design (e.g. use colored badges for active status, use expanding rows to show more data, etc.)
 */

interface UserManagementProps {
  users: User[];
  organizations: Organization[];
  roles: Role[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
}

export const UsersManagement: React.FC<UserManagementProps> = ({
  users,
  organizations,
  roles,
  isLoading,
  isError,
  errorMessage,
}) => {
  const title = 'Users';
  const isAdmin = useIsAdmin();
  const { canModify } = useCanModify('users');
  useDocumentTitle('Users | QBilling');
  const createQuery = useCreateUser();
  const updateQuery = useUpdateUser();
  const disableQuery = useDisableUser();
  const resendPassword = useResendUserPassword();

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const handleCreateUser = async ({
    table,
    values,
  }: CreateMutationDataParams<User>) => {
    if (!values.organizationID) {
      values.organizationID = null;
    }
    if (!values.providerID) {
      values.providerID = null;
    }

    createMutationData<User>({ table, values, mutation: createQuery });
  };

  const handleEditUser = async ({
    table,
    values,
    row,
  }: UpdateMutationDataParams<User>): Promise<void> => {
    updateMutationData<User>({
      table,
      values,
      row,
      mutation: updateQuery,
    });
  };

  const data = users;
  const columns: MRT_ColumnDef<User>[] = UserColumns(organizations, roles);

  const tableOptions: MRT_TableOptions<User> = {
    data,
    columns,
    state: {
      isLoading: isLoading,
      showAlertBanner: isError,
      showProgressBars: isLoading,
      rowSelection,
    },
    initialState: {
      columnVisibility: {
        phone: false,
        providerID: false,
      },
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableFullScreenToggle: false,
    enableEditing: true,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    selectDisplayMode: 'radio',
    mantineTableBodyRowProps: ({ row, table }) => ({
      onClick: () => {
        table.setRowSelection({ [row.id]: true });
        setRowSelection({ [row.id]: true });
      },
      onDoubleClick: () => {
        if (!canModify) return;
        table.setEditingRow(row);
      },
      style: { cursor: 'pointer' },
    }),
    onRowSelectionChange: setRowSelection,
    onCreatingRowSave: handleCreateUser,
    onEditingRowSave: handleEditUser,
    onEditingRowCancel: ({ table }) => {
      table.resetRowSelection();
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: errorMessage });
    },

    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    renderTopToolbarCustomActions: ({ table }) =>
      UserToolBar({
        table,
        isAdmin,
        canModify,
        disableQuery,
        resendPassword,
      }),
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => {
      return (
        <UserFormModal
          heading="Create user"
          table={table}
          row={row}
          internalEditComponents={internalEditComponents}
          organizations={organizations}
        />
      );
    },
    renderEditRowModalContent: ({ table, row, internalEditComponents }) => {
      return (
        <UserFormModal
          heading="Edit user"
          table={table}
          row={row}
          internalEditComponents={internalEditComponents}
          organizations={organizations}
        />
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        Cell: ({
          table,
          row,
        }: {
          table: MRT_TableInstance<User>;
          row: MRT_Row<User>;
        }) => {
          return (
            <EditRowButton
              table={table}
              row={row}
              type="user"
              disabled={!canModify}
            />
          );
        },
      },
      'mrt-row-select': {
        Cell: ({
          row,
          table,
        }: {
          row: MRT_Row<User>;
          table: MRT_TableInstance<User>;
        }) => {
          const isSelected = !!table.getState().rowSelection[row.id];

          return (
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                const currentSelection = table.getState().rowSelection;
                if (currentSelection[row.id]) {
                  table.setRowSelection({});
                } else {
                  table.setRowSelection({ [row.id]: true });
                }
              }}
              style={{ cursor: 'pointer' }}
            />
          );
        },
      },
    },
  };

  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="usersManagement"
      showAlertBanner={false}
    />
  );
};
